<template>
  <div v-if="data">
    <div v-if="data.tag" class="tag">{{ data.tag }}</div>
    <h2 v-if="data.heading" class="!mb-6 md:!mb-8">
      {{ data.heading }}
    </h2>
    <p v-if="data.description">{{ data.description }}</p>

    <ul
      class="grid grid-cols-1 gap-12 md:grid-cols-2 lg:gap-16"
      :class="{
        'lg:grid-cols-3': !data.columns || data.columns === 3,
        'lg:grid-cols-2': data.columns === 2,
        'lg:grid-cols-4': data.columns === 4,
      }"
    >
      <li
        v-for="card in data.cards"
        :key="card.icon.toString()"
        class="flex flex-1"
      >
        <CardIcon :data="card" :segment="data.segment" />
      </li>
    </ul>

    <p
      v-if="data.disclaimer"
      class="disclaimer mt-12"
      v-html="data.disclaimer"
    ></p>
    <template v-if="contactCta">
      <p class="mt-12 font-medium lg:mt-16">
        If you'd like to experience the Aussie Broadband difference, speak to an
        expert about your connectivity needs today.
      </p>
      <BtnPrimary
        label="Speak to an expert"
        url="#contact"
        size="large"
        arrow="right"
        class="w-max max-w-full"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
type CardSets =
  | "aussieValues"
  | "personalGeneric"
  | "businessGeneric"
  | "enterpriseGeneric"
  | "wholesaleGeneric";

const props = defineProps<{
  cardSet?: CardSets;
  custom?: CustomCardData;
  contactCta?: boolean;
}>();

const cardSets: {
  [key in CardSets]: CustomCardData;
} = {
  aussieValues: {
    tag: "Our values",
    heading: "More than words on a wall",
    description:
      "Our values keep us in check. As we go about working to change the game, we often stop and check our choices against our values as you would a compass – making sure we are going the right way.",
    segment: "personal",
    cards: [
      {
        title: "Don't be ordinary, be awesome",
        description:
          "We aren't happy to just be ordinary. Each and every one of us will always go the extra mile to delight our customers rather than just satisfy them. We go out of our way to help each other be awesome and we build each other up; we do not tear people down.",
        icon: resolveComponent("IconAboutBeAwesome"),
      },
      {
        title: "Think big",
        description:
          "We love taking on big challenges and bring progressive thinking to the table. If someone tells us it can't be done, that's when we do our best work. We're always questioning the status quo, we never stand still, and we regard failure as a step towards success.",
        icon: resolveComponent("IconAboutThinking"),
      },
      {
        title: "No bullsh*t",
        description:
          "Be honest, be transparent, be respectful. Remember, 'no bullsh*t' requires equal parts brains (what to say), thoughtfulness (when to say it), and caring (how it's said).",
        icon: resolveComponent("IconAboutNoBS"),
      },
      {
        title: "Be good to people",
        description:
          "We help our customers to succeed. We empower our staff, and anyone at any level can contribute ideas. We have each other's backs. We treat each other like adults, not children, with trust as our default position. We make a positive contribution to our community. And we know the difference between being nice to people and being good to people.",
        icon: resolveComponent("IconCommunityHelp"),
      },
      {
        title: "Have fun",
        description:
          "Life is too short to be not having your sort of fun at work. We don't take ourselves too seriously, we love a joke, and we're playful with our customers and each other where appropriate. We celebrate milestones wherever we can. Belly laughs are part of our business.",
        icon: resolveComponent("IconParty"),
      },
    ],
  },
  personalGeneric: {
    tag: "Why choose Aussie Broadband?",
    heading: "Join Australia's Most Trusted Telco*",
    description:
      "You can rest assured with Aussie Broadband knowing that with us you'll be backed by all these awesome features:",
    disclaimer: "*Per Roy Morgan Trusted Brand Awards 2024.",
    segment: "personal",
    cards: [
      {
        title: "Australia's Most Trusted Telco",
        description:
          "We've won Roy Morgan's Most Trust Telco award 3 years running. So, when we say people trust us, we mean it.",
        icon: resolveComponent("IconHelp"),
      },
      {
        title: "100% Australian-based",
        description:
          "Our local legends have you covered if you ever need assistance or just want a chat.",
        icon: resolveComponent("IconAustralianBased"),
      },
      {
        title: "Quick & easy setup",
        description:
          "Switch in minutes, with no lock-in contracts and hassle-free installation.",
        icon: resolveComponent("IconEasy"),
      },
      {
        title: "Award-winning customer service",
        description:
          "We've won customer service awards from Canstar, Choice and Roy Morgan for our expert customer service.",
        icon: resolveComponent("IconAward"),
      },
      {
        title: "Simple account management",
        description:
          "Account management is quick and easy with our MyAussie app so you're always in control.",
        icon: resolveComponent("IconControl"),
      },
      {
        title: "Thousands of 5-star reviews",
        description:
          "Our quality speaks for itself through thousands of 5-star reviews from our satisfied customers.",
        icon: resolveComponent("IconStar"),
      },
    ],
  },
  businessGeneric: {
    tag: "Why choose Aussie Broadband?",
    heading:
      "A great deal is more than just the bottom line. Here's why you should choose us:",
    description:
      "Hundreds of thousands of happy customers all over Australia put their trust in Aussie Broadband's reliable network.",
    segment: "business",
    columns: 4,
    cards: [
      {
        title: "100% Australian-based call centre",
        description:
          "When you need support, you'll always speak to a real human at one of our Australian call centres.",
        icon: resolveComponent("IconAustralianBased"),
      },
      {
        title: "Flexible business nbn® & phone plans",
        description:
          "Pay month-to-month on our massive range of internet, mobile, and phone plans to suit every business.",
        icon: resolveComponent("IconDocument"),
      },
      {
        title: "Reliable network made for business",
        description:
          "Using high-speed technology, our reliable network was designed for Australian business.",
        icon: resolveComponent("IconGlobe"),
      },
      {
        title: "Easy to switch, easy to manage",
        description:
          "Switching to our nbn® or phone plans is quick and easy so you won't have to worry about downtime.",
        icon: resolveComponent("IconUsers"),
      },
    ],
  },
  enterpriseGeneric: {
    tag: "Why choose Aussie Broadband?",
    heading: "All of our Enterprise customers receive these great benefits.",
    description: "",
    segment: "enterprise",
    cards: [
      {
        title: "Risk management.",
        description:
          "Our focus on security ensures your IT environment, business and customers will be safeguarded by robust solutions backed by globally recognised vendors.",
        icon: resolveComponent("IconList"),
      },
      {
        title: "Technical expertise.",
        description:
          "Take advantage of the knowledge and know-how of our highly skilled team who 'understands' enterprise tech challenges and the solutions to fuel your growth.",
        icon: resolveComponent("IconSpanner"),
      },
      {
        title: "End-to-end confidence.",
        description:
          "We'll work as an extension of your own IT team from consultation through to implementation and operation. Prioritise strategic objectives with the peace of mind.",
        icon: resolveComponent("IconEndToEnd"),
      },
      {
        title: "On-shore support.",
        description:
          "Enjoy personalised support from Australian-based teams. Our experts are readily available to address your concerns, provide timely resolutions, and offer on-site support when needed.",
        icon: resolveComponent("IconAustralianBased"),
      },
      {
        title: "Tech you can rely on.",
        description:
          "Benefit from high availability, high redundancy and minimal disruption. Our proactive approach to maintenance ensures you can confidently rely on your infrastructure.",
        icon: resolveComponent("IconFibre"),
      },
      {
        title: "High performance solutions.",
        description:
          "Harness the power of highly optimised tech for cloud, networking, telecommunications and security to deliver the performance your organisation needs to drive success.",
        icon: resolveComponent("IconRocket"),
      },
    ],
  },
  wholesaleGeneric: {
    tag: "Why choose Aussie Broadband?",
    heading: "Work with us and unlock growth.",
    description: "",
    segment: "wholesale",
    columns: 4,
    cards: [
      {
        title: "Competitive pricing",
        description:
          "Access pricing that meets demand from our range of services.",
        icon: resolveComponent("IconCoins"),
      },
      {
        title: "Pre-sales engineers",
        description:
          "Dedicated wholesale pre-sales network engineers ready to support your needs.",
        icon: resolveComponent("IconSpanner"),
      },
      {
        title: "Simplify your telco",
        description:
          "Access voice and data solutions for sites around the country with a single agreement.",
        icon: resolveComponent("IconDescription"),
      },
      {
        title: "Expertise available ",
        description:
          "Service delivery managers, dedicated project managers and platform experts available when you need them.",
        icon: resolveComponent("IconHelp"),
      },
    ],
  },
};

const data = props.cardSet ? cardSets[props.cardSet] : props.custom;
</script>
